import {useEffect, useMemo, useState} from "react";
import {usePage} from "@inertiajs/react";
import {useCatalogFiltersDrawer} from "../model/useCatalogFiltersDrawer";
import {SelectedFilter} from "@/entities/catalog";
import {CatalogSelectedFilters} from "@/features/catalog-selected-filters";
import {
    CatalogRangeFilter,
    Filter,
    mapCatalogFilter,
    CatalogOptionsFilterDrawer,
    isFilterVisible
} from "@/features/catalog-filters";
import {Svg} from "@/shared/ui/Svg";
import {Button} from "@/shared/ui/Button";
import {declOfNum} from "@/shared/lib/number/declOfNum";
import {useCatalogFilterDrawer} from "@/widgets/catalog-filter-drawer";
import styles from "./CatalogFiltersDrawer.module.scss";
import {Drawer} from "@mui/material"; // Legacy
import {observer} from "mobx-react"; // Legacy
import rootStores from "@/Stores/RootStores"; // Legacy

type PageProps = {
    filters: Filter[];
    isDesktop: boolean;
    selectedFilters: SelectedFilter[];
    insertColors?: Record<string, string>;
};

export const CatalogFiltersDrawer = observer(() => {
    const {filters = [], isDesktop = false, selectedFilters = [], insertColors} = usePage<PageProps>().props;

    // Состояние (отображает индикатор в процессе запроса на сервер)
    const [loadingOptionId, setLoadingOptionId] = useState<number | null>(null);

    const opened = useCatalogFiltersDrawer(s => s.opened);
    const closeCatalogFilters = useCatalogFiltersDrawer(s => s.actions.closeModal);
    const openCatalogFilter = useCatalogFilterDrawer(s => s.actions.openModal);

    const {catalogStore} = rootStores;

    // Подготовка фильтров
    const preparedFilters = useMemo(() => {
        return filters
            .map(filter => mapCatalogFilter({filter, isDesktop, withVisibility: false}))
            .filter(isFilterVisible);
    }, [filters, isDesktop]);

    useEffect(() => {
        if (loadingOptionId !== null) {
            setLoadingOptionId(null);
        }
    }, [filters]);

    const resetAllFilters = () => {
        catalogStore.resetAllFilters();
    }

    const onHandleClick = (option, dependencies) => {
        if (option.disabled) {
            return;
        }

        setLoadingOptionId(option.id);
        catalogStore.onChangeFilter(option, dependencies);
    }

    return (
        <Drawer
            id={`drawer`}
            anchor={`right`}
            open={opened}
            onClose={closeCatalogFilters}
            className={styles.catalogFiltersDrawer}
            classes={{
                paper: styles.drawerPaper
            }}
        >
            <div className={styles.container}>
                <div className={styles.header}>
                    <button type={`button`} className={styles.backButton} onClick={closeCatalogFilters}>
                        <Svg id={`arrowDown`} className={styles.arrowIcon} />
                    </button>
                    <p className={styles.filterName}>Фильтры</p>
                    <button type={`button`} className={styles.resetAllFiltersButton} onClick={resetAllFilters}>
                        Сбросить
                    </button>
                </div>
                <div className={styles.body}>
                    {!isDesktop && (
                        <CatalogSelectedFilters size={`m`} className={styles.chips} selectedFilters={selectedFilters} />
                    )}
                    <div className={styles.filtersContainer}>
                        {preparedFilters.map((filter) => {
                            return (
                                <div key={filter.key} className={styles.filterContainer}>
                                    <div className={styles.titleContainer}>
                                        <p className={styles.title}>{filter.title ?? "Фильтр"}</p>
                                        {filter.type !== 'range' && filter.options.length > filter.visibleOptionsCount && (
                                            <button
                                                type={`button`}
                                                className={styles.allButton}
                                                onClick={() => openCatalogFilter(filter.key)}
                                            >
                                                Все
                                            </button>
                                        )}
                                    </div>
                                    {filter.type === "range" ? (
                                        <CatalogRangeFilter
                                            option={filter.options}
                                            onChange={(values) => {
                                                catalogStore.onBlur(values, filter.options);
                                            }}
                                        />
                                    ) : (
                                        <CatalogOptionsFilterDrawer
                                            loadingOptionId={loadingOptionId}
                                            options={filter.options}
                                            visibleOptionsCount={filter.visibleOptionsCount}
                                            insertColors={insertColors}
                                            onClick={(option) => onHandleClick(option, filter.dependencies)}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className={styles.footer}>
                    <Button fullWidth size={`xl`} onClick={closeCatalogFilters}>
                        {catalogStore.catalogCount > 0 ?
                            <>
                                Показать {catalogStore.catalogCount.toLocaleString()} {declOfNum(catalogStore.catalogCount, [' товар', ' товара', ' товаров'])}
                            </>
                            : 'Товары не найдены'
                        }
                    </Button>
                </div>
            </div>
        </Drawer>
    );
});
